import * as MessengerActions from './messenger.actions';

import {createEntityAdapter, EntityAdapter} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import {AdminPermissionsModel, GroupMemberModel, MessageModel, MessagesPaginationModel, MessengerGroup} from '@messenger/_models/messenger.models';
import {MessengerHelperService} from '@messenger/_services/messenger.helper.service';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {KeyWordModel} from '@common/models/commonHTTP.models';
import {EntityStateModel} from '@common/models/entity-state.models';

export const messengerFeatureKey = 'messenger';

// TODO: OS fix sorting ALL PLATFORMS include server response
export function sortGroups(a: MessengerGroup, b: MessengerGroup) {
    return MessengerHelperService.sortGroupsComparer(a, b);
}

export function sortMessages(a: MessageModel<any>, b: MessageModel<any>) {
    return StoredComponentHelperService.sortArrayComparer(a, b, 'index', 'ASC');
}

export const groupsAdapter: EntityAdapter<MessengerGroup> = createEntityAdapter<MessengerGroup>({
    sortComparer: sortGroups
});

export const messagesAdapter: EntityAdapter<MessageModel<any>> = createEntityAdapter<MessageModel<any>>({
    sortComparer: sortMessages
});

export interface LoadedGroupProps {
    groupId: string;
    scrollPosition?: number;
    messagesLoaded?: boolean;
    newMessage?: MessageModel<any>;
}

export interface GroupAdministrativeData {
    mainAdminUserId?: string,
    admins?: Record<string, GroupMemberModel>,
    permissions?: AdminPermissionsModel
}

export interface LoadedGroupsState extends EntityStateModel<MessageModel<any>, any> {
    props?: LoadedGroupProps;
    administrativeData?: GroupAdministrativeData;
    pagination: MessagesPaginationModel;
}

export interface MessengerState {
    groupsList: EntityStateModel<MessengerGroup, KeyWordModel>;
    loadedGroups: Record<string, LoadedGroupsState>,
    hasUnreadMessages?: boolean;
}

const initialState: MessengerState = {
    groupsList: groupsAdapter.getInitialState({
        listLoading: 'listLoading',
        pagination: {},
        itemsLoading: []
    }),
    loadedGroups: {}
};

export const messengerReducer = createReducer(
    initialState,

    // <editor-fold desc="Groups">

    on(MessengerActions.LoadGroups, (state, action) => ({
        ...state,
        groupsList: {
            ...state.groupsList,
            listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.groupsList?.ids)
        }
    })),

    on(MessengerActions.LoadGroupsSuccess, (state, action) => {
        return {
            ...state,
            groupsList: action.payload.clearState
                ? groupsAdapter.setAll(action.payload.items, {
                    ...state.groupsList,
                    pagination: action.payload.pagination,
                    listLoading: undefined

                })
                : groupsAdapter.upsertMany(action.payload.items, {
                    ...state.groupsList,
                    pagination: action.payload.pagination,
                    listLoading: undefined
                })
        };
    }),

    on(MessengerActions.LoadGroupsFailure, (state) => ({
        ...state,
        groupsList: {
            ...state.groupsList,
            listLoading: undefined
        }
    })),

    on(MessengerActions.SetGroupsFilter, (state, action) => ({
        ...state,
        groupsList: {
            ...state.groupsList,
            filter: action.payload.filter
        }
    })),

    on(MessengerActions.SetGroupsPagination, (state, action) => ({
        ...state,
        groupsList: {
            ...state.groupsList,
            pagination: {
                ...state.groupsList?.pagination,
                number: action.payload?.next
                    ? StoredComponentHelperService.setNextPage(state.groupsList?.pagination, state.groupsList?.ids?.length)
                    : 0
            }
        }
    })),

    on(MessengerActions.SetGroupsLoading, (state, action) => ({
        ...state,
        groupsList: {
            ...state.groupsList,
            listLoading: action.payload.loading
        }
    })),

    on(MessengerActions.UpsertGroupSuccess, (state, action) => ({
        ...state,
        groupsList: groupsAdapter.upsertOne(action.payload.item, {
            ...state.groupsList,
            pagination: {
                ...state.groupsList.pagination,
                totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state.groupsList.pagination, state.groupsList?.ids)
            }
        })
    })),

    on(MessengerActions.UpdateGroupSuccess, (state, action) => ({
        ...state,
        groupsList: groupsAdapter.updateOne({id: action.payload.item.id!, changes: {...action.payload.item}}, {
            ...state.groupsList
        })
    })),

    on(MessengerActions.DeleteGroupSuccess, (state, action) => ({
        ...state,
        groupsList: groupsAdapter.removeOne(action.payload.groupId, {
            ...state.groupsList,
            pagination: {
                ...state.groupsList.pagination,
                totalElements: StoredComponentHelperService.setTotalElements(action.payload.groupId!, state.groupsList.pagination, state.groupsList?.ids, true)
            }
        })
    })),

    on(MessengerActions.UpdateGroupLastMessageSuccess, (state, action) => {
        return {
            ...state,
            groupsList: groupsAdapter.updateOne({
                id: action.payload.item.groupId!,
                changes: {
                    lastMessage: action.payload.item,
                    lastMessageIndex: action.payload.item.index,
                    ...action.payload.incrementUnread && {
                        totalUnread: (state.groupsList.entities[action.payload.item!.groupId!]?.totalUnread as number) + 1
                    },
                    ...!action.payload.incrementUnread && {
                        lastSentMessageIndex: action.payload.item.index,
                    }
                }
            }, {...state.groupsList}),
        };
    }),

    on(MessengerActions.SetViewedPreviousMessages, (state, action) => {
        return {
            ...state,
            loadedGroups: {
                ...state.loadedGroups,
                [action.payload.groupId]: messagesAdapter.updateMany(
                    action.payload.prevUnViewedMessages.map(id => ({
                        id,
                        changes: {status: 'VIEWED'}
                    })),
                    {...state.loadedGroups[action.payload.groupId!]}
                )
            }
        };
    }),

    // </editor-fold desc="Groups">

    // <editor-fold desc="Loaded group">

    on(MessengerActions.CheckGroupMessagesState, (state, action) => {
        if (!state.loadedGroups[action.payload.groupId]) {
            return {
                ...state,
                loadedGroups: {
                    ...state.loadedGroups,
                    [action.payload.groupId]: messagesAdapter.getInitialState({
                        ...state.loadedGroups[action.payload.groupId]
                    })
                }
            };
        } else {
            return {...state};
        }
    }),

    on(MessengerActions.UpdateGroupPagination, (state, action) => {
        return {
            ...state,
            loadedGroups: {
                ...state.loadedGroups,
                [action.payload.groupId]: {
                    ...state.loadedGroups[action.payload.groupId],
                    pagination: {
                        ...state.loadedGroups[action.payload.groupId].pagination,
                        firstNumber: state.loadedGroups[action.payload.groupId].pagination.firstNumber + action.payload.pageDiff,
                        lastNumber: state.loadedGroups[action.payload.groupId].pagination.lastNumber + action.payload.pageDiff,
                    }
                }
            }
        };
    }),

    on(MessengerActions.LoadMessages, (state, action) => {
        if (state.loadedGroups[action.payload.groupId].props?.messagesLoaded && !action.payload.clearState && !action.payload.direction) {
            return {...state};
        }
        return {
            ...state,
            loadedGroups: {
                ...state.loadedGroups,
                [action.payload.groupId]: {
                    ...state.loadedGroups[action.payload.groupId],
                    listLoading: action.payload.loading
                }
            }
        };
    }),

    on(MessengerActions.LoadMessagesSuccess, (state, action) => {

        if (action.payload.clearState || !state.loadedGroups[action.payload.groupId].ids.length || !action.payload.items.length) {
            return {
                ...state,
                loadedGroups: {
                    ...state.loadedGroups,
                    [action.payload.groupId]: messagesAdapter.setAll(action.payload.items, {
                        ...state.loadedGroups[action.payload.groupId],
                        pagination: {
                            ...action.payload.pagination,
                            lastNumber: action.payload.pagination.number ?? 0,
                            firstNumber: action.payload.pagination.number ?? 0,
                        },
                        listLoading: undefined,
                        props: {
                            ...state.loadedGroups[action.payload.groupId].props,
                            groupId: action.payload.groupId,
                            messagesLoaded: true
                        }
                    })
                }
            };
        }
        return {
            ...state,
            loadedGroups: {
                ...state.loadedGroups,
                [action.payload.groupId]: messagesAdapter.setAll(MessengerHelperService.setMessages(state.loadedGroups[action.payload.groupId], action.payload.items, action.payload.pagination, action.payload.direction), {
                    ...state.loadedGroups[action.payload.groupId],
                    pagination: MessengerHelperService.setPagination(action.payload.pagination, action.payload.direction!),
                    listLoading: undefined
                })
            }
        };
    }),

    on(MessengerActions.LoadMessagesFailure, (state, action) => ({
        ...state,
        loadedGroups: {
            ...state.loadedGroups,
            [action.payload.groupId]: {
                ...state.loadedGroups[action.payload.groupId],
                listLoading: undefined
            }
        }
    })),

    on(MessengerActions.ClearLoadedGroup, (state, action) => {
        if (!state.loadedGroups[action.payload.groupId]) {
            return {...state};
        }

        return {
            ...state,
            loadedGroups: {
                ...state.loadedGroups,
                [action.payload.groupId]: messagesAdapter.removeAll({
                    ...state.loadedGroups[action.payload.groupId],
                    pagination: {
                        firstNumber: 0,
                        lastNumber: 0,
                    },
                    props: {
                        groupId: action.payload.groupId,
                        messagesLoaded: false,
                        scrollPosition: 0
                    },
                })
            }
        };
    }),

    on(MessengerActions.DeleteGroupSuccess, (state, action) => {
        if (!state.loadedGroups[action.payload.groupId]) {
            return {...state};
        }

        const loadedGroups = {...state.loadedGroups};
        delete loadedGroups[action.payload.groupId];

        return {...state, loadedGroups: loadedGroups};
    }),

    // </editor-fold desc="Loaded group">

    // <editor-fold desc="Loaded groups settings">
    on(MessengerActions.UpdateGroupAdminsSuccess, (state, action) => ({
        ...state,
        loadedGroups: {
            ...state.loadedGroups,
            [action.payload.groupId]: {
                ...state.loadedGroups[action.payload.groupId],
                administrativeData: action.payload.data
            }
        }
    })),

    on(MessengerActions.UpdateGroupProps, (state, action) => ({
        ...state,
        loadedGroups: {
            ...state.loadedGroups,
            [action.payload.groupId]: {
                ...state.loadedGroups[action.payload.groupId],
                props: {
                    groupId: action.payload.groupId,
                    ...state.loadedGroups[action.payload.groupId].props,
                    ...action.payload.scrollPosition !== undefined && {scrollPosition: action.payload.scrollPosition},
                    ...action.payload.messagesLoaded && {messagesLoaded: action.payload.messagesLoaded},
                    ...action.payload.newMessage && {newMessage: action.payload.newMessage}
                }
            }
        }
    })),

    // </editor-fold desc="Loaded groups settings">

    // <editor-fold desc="Message item">

    on(MessengerActions.SetMessagesListLoading, (state, action) => ({
        ...state,
        loadedGroups: {
            ...state.loadedGroups,
            [action.payload.groupId]: {
                ...state.loadedGroups[action.payload.groupId],
                listLoading: action.payload.loading
            }
        }
    })),

    on(MessengerActions.UpsertMessageSuccess, (state, action) => {
        return {
            ...state,
            loadedGroups: {
                ...state.loadedGroups,
                ...state.loadedGroups[action.payload.item.groupId!].pagination?.first && {
                    [action.payload.item.groupId!]: messagesAdapter.upsertOne(action.payload.item, {
                        ...state.loadedGroups[action.payload.item.groupId!],
                        pagination: {
                            ...state.loadedGroups[action.payload.item.groupId!].pagination,
                            totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state.loadedGroups[action.payload.item.groupId!].pagination, state.loadedGroups[action.payload.item.groupId!]?.ids)
                        }
                    })
                }
            }
        };
    }),

    on(MessengerActions.UpdateMessageSuccess, (state, action) => {
        return {
            ...state,
            loadedGroups: {
                ...state.loadedGroups,
                [action.payload.item.groupId!]: messagesAdapter.updateOne({
                    id: action.payload.item.id!,
                    changes: action.payload.item
                }, {...state.loadedGroups[action.payload.item.groupId!]})
            }
        };
    }),

    on(MessengerActions.UpdateMessagesMultipleSuccess, (state, action) => {
        return {
            ...state,
            loadedGroups: {
                ...state.loadedGroups,
                [action.payload.groupId!]: messagesAdapter.updateMany(action.payload.items, {...state.loadedGroups[action.payload.groupId!]})
            }
        };
    }),

    on(MessengerActions.SendMessageSuccess, (state, action) => {
        return {
            ...state,
            loadedGroups: {
                ...state.loadedGroups,
                [action.payload.groupId!]: state.loadedGroups[action.payload.groupId!].pagination.first
                    ? messagesAdapter.addOne(action.payload.message, {
                        ...state.loadedGroups[action.payload.groupId!],
                        listLoading: undefined
                    })
                    : {
                        ...state.loadedGroups[action.payload.groupId!],
                        listLoading: undefined
                    }
            }
        };
    }),

    on(MessengerActions.SendMessageFailure, (state, action) => ({
        ...state,
        loadedGroups: {
            ...state.loadedGroups,
            [action.payload.groupId]: {
                ...state.loadedGroups[action.payload.groupId],
                listLoading: undefined
            }
        }
    })),

    on(MessengerActions.EditMessage, (state, action) => ({
        ...state,
        loadedGroups: {
            ...state.loadedGroups,
            [action.payload.groupId]: {
                ...state.loadedGroups[action.payload.groupId],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.loadedGroups[action.payload.groupId].itemsLoading, action.payload.message.id, true)
            }
        }
    })),

    on(MessengerActions.EditMessageSuccess, (state, action) => {
        return {
            ...state,
            loadedGroups: {
                ...state.loadedGroups,
                [action.payload.groupId!]: messagesAdapter.updateOne(
                    {id: action.payload.message.id, changes: action.payload.message}, {
                        ...state.loadedGroups[action.payload.groupId!],
                        itemsLoading: StoredComponentHelperService.updateItemLoading(state.loadedGroups[action.payload.groupId].itemsLoading, action.payload.message.id, false),
                        listLoading: undefined
                    })
            }
        };
    }),

    on(MessengerActions.SetMessageItemLoading, (state, action) => ({
        ...state,
        loadedGroups: {
            ...state.loadedGroups,
            [action.payload.groupId]: {
                ...state.loadedGroups[action.payload.groupId],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.loadedGroups[action.payload.groupId].itemsLoading, action.payload.messageId, action.payload.isStart),
                listLoading: undefined
            }
        }
    })),

    // </editor-fold desc="Message item">

    on(MessengerActions.SetInitialState, () => ({...initialState}))
);

export const {selectAll: selectAllGroups} = groupsAdapter.getSelectors();
export const {selectIds: selectGroupsIds} = groupsAdapter.getSelectors();
export const selectAllMessages = (groupId: string) => messagesAdapter.getSelectors((state: MessengerState) => state.loadedGroups[groupId])?.selectAll;
